import type { QueryClient } from '@tanstack/vue-query'
import type { RuleSink, Transport, TransportStatus } from '@twinepos/vue-rbac'

import { QueryObserver } from '@tanstack/vue-query'
import { watch } from 'vue'

/** Default freshness window (ms) before fetched rules trigger a background revalidate. */
const DEFAULT_STALE_TIME_MS = 5 * 60_000

/** Prefix shared by every abilities query key, so `refresh()` can invalidate the family. */
export const ABILITIES_QUERY_KEY = ['rbac', 'abilities'] as const

export interface HttpTransportOptions {
  /** Abilities endpoint, e.g. `/admin/me/abilities`. */
  endpoint: string
  /** The app's existing Vue Query client (reuse `support/query`). */
  queryClient: QueryClient
  /**
   * Everything that identifies WHOSE abilities these are — tenant and user.
   *
   * This is not an optimisation. Entries are kept for the session (`gcTime:
   * Infinity`), so a key that omits the tenant serves the previous tenant's
   * rules after a switch: the UI renders affordances for a tenant the user has
   * left, until the next revalidate. The controller catches that and fails
   * closed, but only after the wrong payload has been applied once — keying it
   * correctly means it never is.
   *
   * **Read reactively.** This is re-evaluated whenever the reactive state it
   * reads changes, and the observer re-points at the new key. A thunk over plain
   * values still works and simply never re-keys — which is the trap this exists
   * to close: in an Inertia app, signing in is a visit rather than a page load,
   * so a key captured once outlives the person it names.
   */
  cacheKey?: () => readonly (string | number | null | undefined)[]
  /**
   * Whether anyone is signed in. Defaults to true.
   *
   * A guest holds no abilities, and an endpoint behind an auth stack refuses
   * their fetch with a 401. So while this is false the transport holds no rules
   * and sends nothing — not on start, and not on focus, reconnect or `refresh()`.
   *
   * **Read reactively**, like `cacheKey`: the transport fetches as soon as this
   * turns true, which in an Inertia app is the sign-in visit.
   */
  isSignedIn?: () => boolean
  /** How long fetched rules stay fresh before a background revalidate. */
  staleTime?: number
  /** Extra headers per request (e.g. the host app's auth token). */
  headers?: () => Record<string, string>
}

const isJson = (response: Response): boolean =>
  (response.headers.get('content-type') ?? '').toLowerCase().includes('json')

/**
 * Query keys are compared by value, not identity: the getter builds a fresh array
 * on every read, so a reference check would re-key on every reactive tick.
 */
const sameKey = (left: readonly unknown[], right: readonly unknown[]): boolean =>
  left.length === right.length && left.every((part, index) => part === right[index])

/** Whose abilities the observer is pointed at — or nobody, while `isSignedIn` is false. */
interface Principal {
  key: readonly unknown[]
  isSignedIn: boolean
}

const samePrincipal = (left: Principal, right: Principal): boolean =>
  left.isSignedIn === right.isSignedIn && sameKey(left.key, right.key)

/** The status until the first payload lands: loading for somebody signed in, idle for nobody. */
const pendingStatus = (principal: Principal): TransportStatus => (principal.isSignedIn ? 'loading' : 'idle')

/**
 * Vue Query–backed transport. Uses a `QueryObserver` (not the `useQuery`
 * composable) so it works outside a component setup — the plugin installs it at
 * app bootstrap. Honours `If-None-Match`/304 to revalidate cheaply, and refetches
 * on reconnect/focus.
 *
 * The transport is dumb: it hands raw JSON to the sink, which is the single
 * validation choke point. It knows the wire only well enough to read `version`
 * back out for the next `If-None-Match`.
 */
export const createHttpTransport = (options: HttpTransportOptions): Transport => {
  const staleTime = options.staleTime ?? DEFAULT_STALE_TIME_MS

  let lastRaw: unknown = null
  let lastVersion: string | null = null
  let unsubscribe: (() => void) | null = null
  let stopWatchingPrincipal: (() => void) | null = null

  const queryKey = (): unknown[] => [...ABILITIES_QUERY_KEY, ...(options.cacheKey?.() ?? ['self'])]

  /** Reads `cacheKey` and `isSignedIn`, so a watcher over it follows both. */
  const currentPrincipal = (): Principal => ({ key: queryKey(), isSignedIn: options.isSignedIn?.() ?? true })

  const queryFn = async (): Promise<unknown> => {
    const headers: Record<string, string> = { Accept: 'application/json', ...options.headers?.() }

    if (lastVersion) {
      headers['If-None-Match'] = `"${lastVersion}"`
    }

    const response = await fetch(options.endpoint, { headers, credentials: 'include' })

    if (response.status === 304 && lastRaw) {
      return lastRaw
    }

    if (!response.ok) {
      throw new Error(`[rbac] abilities request failed: ${response.status}`)
    }

    // The endpoint sits behind the admin auth stack, so an expired session
    // answers with a redirect to the login page — a 200 carrying HTML. Parsing
    // that as JSON would throw something unreadable; saying so plainly is the
    // difference between a five-minute and a fifty-minute debug.
    if (!isJson(response)) {
      throw new Error('[rbac] abilities endpoint returned a non-JSON response (is the session still authenticated?)')
    }

    const json: unknown = await response.json()
    lastRaw = json

    if (json && typeof json === 'object' && 'version' in json) {
      lastVersion = String((json as { version: unknown }).version)
    }

    return json
  }

  /**
   * One shape for the initial observer and every re-key, so the two cannot drift.
   *
   * `enabled` is what stops a guest's requests. A disabled observer does not fetch
   * on subscribe, focus or reconnect, and invalidation does not refetch it.
   */
  const observerOptions = (principal: Principal) => ({
    queryKey: principal.key,
    queryFn,
    enabled: principal.isSignedIn,
    staleTime,
    gcTime: Infinity,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })

  return {
    start(sink: RuleSink) {
      let active = currentPrincipal()

      sink.setStatus(pendingStatus(active))

      const observer = new QueryObserver(options.queryClient, observerOptions(active))

      unsubscribe = observer.subscribe(result => {
        // A disabled observer still reports what the cache holds under its key.
        // While nobody is signed in the transport holds no rules, so none of it
        // reaches the sink.
        if (!active.isSignedIn) {
          return
        }

        if (result.isError) {
          sink.setStatus('error')
          return
        }

        if (result.data !== undefined) {
          sink.update(result.data)
        }
      })

      /**
       * The principal can change without a page load. In an Inertia app, signing
       * in, signing out and switching user are all visits, so the host's bootstrap
       * never re-runs. Held fixed, the principal read above would stay whoever held
       * the tab first. Most visibly that is a guest, and every gated affordance then
       * stays hidden until a manual reload.
       *
       * Re-pointing the observer is what Vue Query itself does for a reactive
       * key. `flush: 'sync'` because a render between the identity changing and
       * the observer following it would be a render against the wrong
       * principal's rules.
       */
      stopWatchingPrincipal = watch(
        currentPrincipal,
        next => {
          if (samePrincipal(active, next)) {
            return
          }

          active = next

          // Conditional-request state belongs to the principal we just left.
          // Kept, the next fetch sends their ETag for somebody else's abilities,
          // and a 304 would replay their payload under the new key.
          lastRaw = null
          lastVersion = null

          // Fail closed across the switch: drop the previous principal's rules
          // before the new ones land, never after.
          sink.clear()
          sink.setStatus(pendingStatus(next))

          observer.setOptions(observerOptions(next))
        },
        { flush: 'sync' }
      )
    },
    refresh: () => options.queryClient.invalidateQueries({ queryKey: [...ABILITIES_QUERY_KEY] }),
    stop: () => {
      unsubscribe?.()
      unsubscribe = null
      stopWatchingPrincipal?.()
      stopWatchingPrincipal = null
    }
  }
}
